// useDismiss.js
// Client: vmfive
// Shared dismiss hook used by SelectPill, Kebab, and SplitButton.
// Closes the popover on outside mousedown or Escape key.
//
// Usage:
//   const [triggerRef, popRef] = useDismiss(open, onClose);
//
// Attach triggerRef to the trigger wrapper element and popRef to the portal
// popover element. Both refs are checked: a click is "outside" only when it
// is contained by neither element. This supports the portal pattern where the
// popover lives in document.body and has no DOM ancestor relationship with the
// trigger wrapper.

import { useRef, useEffect } from 'react';

/**
 * @param {boolean}   open    Whether the popover is currently open.
 * @param {Function}  onClose Called when an outside mousedown or Escape is detected.
 * @returns {[React.RefObject, React.RefObject]} [triggerRef, popRef]
 */
export default function useDismiss(open, onClose) {
  const triggerRef = useRef(null);
  const popRef = useRef(null);

  useEffect(() => {
    if (!open) return;

    const onMouseDown = (e) => {
      const inTrigger = triggerRef.current && triggerRef.current.contains(e.target);
      const inPop = popRef.current && popRef.current.contains(e.target);
      if (!inTrigger && !inPop) onClose();
    };
    const onKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };

    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open, onClose]);

  return [triggerRef, popRef];
}
